import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  ReactNode,
} from 'react';

import {
  AuthContextType,
  AuthProvider as AuthProviderType,
  AuthState,
  LoginCredentials,
  User,
} from '../types/auth';
import { authService } from '../services';
import { API_BASE_URL, USE_MOCKS } from '../config/serviceConfig';

// ─── Storage keys ────────────────────────────────────────────
const STORAGE_KEY_USER = 'bp_user';
const STORAGE_KEY_TOKEN = 'bp_token';
const STORAGE_KEY_SIG = 'bp_sig';

// ─── Firma anti-manomissione ─────────────────────────────────
// Rileva modifiche manuali al localStorage (es. Cambio ruolo da DevTools).
// In produzione la vera sicurezza è il token Bearer verificato dal server.
const SECRET = process.env.REACT_APP_INTEGRITY_SECRET ?? 'bp-integrity-2025';

const signUser = (user: User): string => {
  const payload = `${user.id}|${user.role}|${user.email}|${SECRET}`;
  return btoa(unescape(encodeURIComponent(payload)));
};

const verifyUser = (user: User, sig: string): boolean => {
  try {
    return signUser(user) === sig;
  } catch {
    return false;
  }
};

// ─── Helpers storage ─────────────────────────────────────────
const persistSession = (user: User, token: string, remember = true): void => {
  const storage = remember ? localStorage : sessionStorage;
  // Pulisce l'altro storage per evitare sessioni duplicate
  const other = remember ? sessionStorage : localStorage;
  other.removeItem(STORAGE_KEY_USER);
  other.removeItem(STORAGE_KEY_TOKEN);
  other.removeItem(STORAGE_KEY_SIG);

  storage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
  storage.setItem(STORAGE_KEY_TOKEN, token);
  storage.setItem(STORAGE_KEY_SIG, signUser(user));
};

const clearSession = (): void => {
  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem(STORAGE_KEY_USER);
    s.removeItem(STORAGE_KEY_TOKEN);
    s.removeItem(STORAGE_KEY_SIG);
  });
};

const readSession = (): { user: User; sig: string } | null => {
  for (const storage of [localStorage, sessionStorage]) {
    const stored = storage.getItem(STORAGE_KEY_USER);
    const sig = storage.getItem(STORAGE_KEY_SIG);
    if (stored && sig) return { user: JSON.parse(stored) as User, sig };
  }
  return null;
};

// ─── Stato iniziale ──────────────────────────────────────────
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// ─── Reducer ─────────────────────────────────────────────────
type AuthAction =
  | { type: 'AUTH_INIT_DONE'; payload: User | null }
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'AUTH_RESET_LOADING' }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: Partial<User> };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_INIT_DONE':
      return {
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: action.payload !== null,
      };
    case 'LOGIN_START':
      return { ...state, isLoading: true, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isLoading: false,
        user: null,
        isAuthenticated: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'AUTH_RESET_LOADING':
      return { ...state, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// ─── Provider ────────────────────────────────────────────────
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // ── Ripristina sessione al mount ─────────────────────────
  useEffect(() => {
    try {
      const session = readSession();
      if (session) {
        if (!verifyUser(session.user, session.sig)) {
          console.warn('[Auth] Sessione manomessa. Logout forzato.');
          clearSession();
          dispatch({ type: 'AUTH_INIT_DONE', payload: null });
          return;
        }
        dispatch({ type: 'AUTH_INIT_DONE', payload: session.user });
      } else {
        dispatch({ type: 'AUTH_INIT_DONE', payload: null });
      }
    } catch {
      clearSession();
      dispatch({ type: 'AUTH_INIT_DONE', payload: null });
    }
  }, []);

  // ── Login ────────────────────────────────────────────────
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<User | null> => {
      dispatch({ type: 'LOGIN_START' });
      try {
        const { user, token } = await authService.login(
          credentials.email,
          credentials.password
        );
        persistSession(user, token, credentials.rememberMe ?? true);
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        return user;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Errore durante il login.';
        dispatch({ type: 'LOGIN_FAILURE', payload: message });
        return null;
      }
    },
    []
  );

  // ── Social login (popup OAuth) ───────────────────────────
  const loginWithSocial = useCallback(
    (provider: string): Promise<User | null> => {
      // Mock: bypass popup e restituisce un utente fittizio
      if (USE_MOCKS) {
        dispatch({ type: 'LOGIN_START' });
        return new Promise((resolve) => {
          setTimeout(() => {
            const mockUser: User = {
              id: crypto.randomUUID(),
              email: `mock.${provider}@example.com`,
              firstName: 'Mario',
              lastName: 'Social',
              role: 'user',
              createdAt: new Date().toISOString(),
              emailConfirmed: true,
              authProvider: provider as AuthProviderType,
              accountStatus: 'full',
            };
            persistSession(mockUser, 'mock|social_token');
            dispatch({ type: 'LOGIN_SUCCESS', payload: mockUser });
            resolve(mockUser);
          }, 800);
        });
      }

      return new Promise((resolve, reject) => {
        dispatch({ type: 'LOGIN_START' });

        const baseUrl = API_BASE_URL.replace('/api', '');
        const popup = window.open(
          `${baseUrl}/auth/${provider}/redirect`,
          'social_login',
          'width=520,height=620,left=400,top=80'
        );

        if (!popup) {
          dispatch({ type: 'AUTH_RESET_LOADING' });
          reject(
            new Error(
              'Il browser ha bloccato il popup. Abilita i popup per questo sito.'
            )
          );
          return;
        }

        let settled = false;
        let closedCheck: ReturnType<typeof setInterval>;

        const cleanup = () => {
          window.removeEventListener('message', onMessage);
          clearInterval(closedCheck);
        };

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.source !== popup) return;
          if (settled) return;
          settled = true;
          cleanup();

          const { token, user, error } = (event.data ?? {}) as {
            token?: string;
            user?: User;
            error?: string;
          };

          if (error || !token || !user) {
            dispatch({ type: 'AUTH_RESET_LOADING' });
            reject(new Error(error ?? 'Errore durante il login social.'));
            return;
          }

          persistSession(user, token);
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
          resolve(user);
        };

        window.addEventListener('message', onMessage);

        closedCheck = setInterval(() => {
          if (popup.closed && !settled) {
            settled = true;
            cleanup();
            dispatch({ type: 'AUTH_RESET_LOADING' });
            resolve(null); // popup chiuso dall'utente — nessun errore
          }
        }, 500);
      });
    },
    []
  );

  // ── Login diretto con token già emesso dal backend ───────
  // Usato dal callback dei link ricevuti per email (verifica email,
  // accesso free senza password): a differenza di `login`, qui il
  // backend ha già autenticato l'utente, non serve altra chiamata API.
  const loginWithToken = useCallback(
    (user: User, token: string, rememberMe = true) => {
      persistSession(user, token, rememberMe);
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    },
    []
  );

  // ── Logout ───────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // ignora errori di rete — puliamo lo stato comunque
    } finally {
      clearSession();
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  // ── Clear error ──────────────────────────────────────────
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const updateUser = useCallback((data: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: data });
    // Aggiorna nello storage in cui è stata salvata la sessione
    for (const storage of [localStorage, sessionStorage]) {
      const stored = storage.getItem(STORAGE_KEY_USER);
      if (stored) {
        const updated = { ...JSON.parse(stored), ...data };
        storage.setItem(STORAGE_KEY_USER, JSON.stringify(updated));
        storage.setItem(STORAGE_KEY_SIG, signUser(updated as User));
        break;
      }
    }
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    loginWithSocial,
    loginWithToken,
    logout,
    clearError,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
